import { QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense, useMemo } from 'react'
import { Toaster } from 'react-hot-toast'
import { Provider } from 'react-redux'
import { BrowserRouter, Route, Routes, Navigate, useParams } from 'react-router-dom'

import { CommandPalette } from './components/CommandPalette'
import { PresentationRouteBoundary } from './components/PresentationRouteBoundary'
import { MainLayout } from './components/layout/main-layout'
import { TooltipProvider } from './components/ui/tooltip'
import { useInit } from './hooks/use-init'
import { queryClient } from './lib/query'
import { LoginLayout } from './LoginLayout'
import { selectIsHydrating, selectIsLoggedIn } from './modules/common'
import { useAppSelector } from './modules/core'
import { store } from './store'

// A simple component to handle wildcard redirects while preserving URL parameters
const SplatRedirect = ({ to }: { to: string }) => {
  const params = useParams()
  const splat = params['*'] || ''
  return <Navigate to={splat ? `${to}/${splat}` : to} replace />
}

// Lazy-loaded pages for better code splitting and faster initial load
// Home is loaded eagerly as it's the default landing page
import Home from './pages/Home'

// Playbook pages - Dynamic markdown-based pages
const PlaybookIndex = lazy(() => import('./pages/playbook/PlaybookIndex'))
const PlaybookPage = lazy(() => import('./pages/playbook/PlaybookPage'))
const PlaybookPresentationPage = lazy(() => import('./pages/playbook/PlaybookPresentationPage'))
const PresentationsPage = lazy(() => import('./pages/playbook/PresentationsPage'))
const PresentationDetailsPage = lazy(() => import('./pages/playbook/PresentationDetailsPage'))

// Metrics pages
const BusinessMetrics = lazy(() => import('./pages/BusinessMetrics'))
const Funnel = lazy(() => import('./pages/Funnel'))
const CampaignMetrics = lazy(() => import('./pages/CampaignMetrics'))
const EngagementMetrics = lazy(() => import('./pages/EngagementMetrics'))
const RetentionMetrics = lazy(() => import('./pages/RetentionMetrics'))
const AiCosts = lazy(() => import('./pages/AiCosts'))
const SocialKit = lazy(() => import('./pages/SocialKit'))
const TroiMetrics = lazy(() => import('./pages/TroiMetrics'))
const TakaTV = lazy(() => import('./pages/TakaTV'))
const TakaNews = lazy(() => import('./pages/TakaNews'))
const TakaNewsDetail = lazy(() => import('./pages/TakaNewsDetail'))

// Platform pages
const Users = lazy(() => import('./pages/Users'))
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'))
const Businesses = lazy(() => import('./pages/Businesses'))
const BusinessDetailPage = lazy(() => import('./pages/BusinessDetailPage'))
const Creatives = lazy(() => import('./pages/Creatives'))
const ContentEngine = lazy(() => import('./pages/ContentEngine'))
const SocialGateways = lazy(() => import('./pages/SocialGateways'))
const MetaMigration = lazy(() => import('./pages/MetaMigration'))
const BenchmarkQueue = lazy(() => import('./pages/BenchmarkQueue'))
const BenchmarkScoreboard = lazy(() => import('./pages/BenchmarkScoreboard'))
const SystemHealth = lazy(() => import('./pages/SystemHealth'))
const ClientPolicy = lazy(() => import('./pages/ClientPolicy'))
const Referral = lazy(() => import('./pages/Referral'))
const ApiKeys = lazy(() => import('./pages/ApiKeys'))
const CommandLog = lazy(() => import('./pages/CommandLog'))
const PromoCodes = lazy(() => import('./pages/PromoCodes'))

// Empty fallback for lazy-loaded pages (prevents layout shift)
function PageLoader() {
  return <div className="min-h-[50vh]" />
}

function AppContent() {
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const isHydrating = useAppSelector(selectIsHydrating)

  // Initialize app and authentication
  useInit()

  const routes = useMemo(() => {
    // Hold while boot-time silent refresh is resolving — otherwise authenticated
    // reloads briefly flash the login layout.
    if (isHydrating) {
      return (
        <div className="flex min-h-screen items-center justify-center text-muted-foreground">
          Loading…
        </div>
      )
    }
    if (isLoggedIn) {
      return (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginLayout />} />
            {/* Fullscreen TV route — channel via ?channel= query param */}
            <Route path="/ttv" element={<TakaTV />} />
            <Route path="/" element={<MainLayout />}>
              {/* Home */}
              <Route index element={<Home />} />

              {/* Playbook Routes - Dynamic markdown-based pages */}
              <Route path="playbook">
                <Route index element={<PlaybookIndex />} />
                <Route path="presentations" element={<PresentationsPage />} />
                <Route path="presentations/:deckId" element={<PresentationDetailsPage />} />
                <Route path="present/*" element={<PlaybookPresentationPage />} />
                <Route path="*" element={<PlaybookPage />} />
              </Route>

              {/* Metrics Routes */}
              <Route path="metrics">
                <Route index element={<Navigate to="/metrics/business" replace />} />
                <Route path="business" element={<BusinessMetrics />} />
                <Route path="funnel" element={<Funnel />} />
                <Route path="campaigns" element={<CampaignMetrics />} />
                <Route path="engagement" element={<EngagementMetrics />} />
                <Route path="retention" element={<RetentionMetrics />} />
                <Route path="ai-costs" element={<AiCosts />} />
                <Route path="socialkit" element={<SocialKit />} />
                <Route path="troi" element={<TroiMetrics />} />
                <Route path="taka-tv" element={<TakaTV />} />
                <Route path="taka-news" element={<TakaNews />} />
                <Route path="taka-news/:id" element={<TakaNewsDetail />} />
              </Route>

              {/* Platform Routes */}
              <Route path="platform">
                <Route index element={<Navigate to="/platform/users" replace />} />
                <Route path="users" element={<Users />} />
                <Route path="users/:userId/:tab" element={<UserDetailPage />} />
                <Route path="users/:userId" element={<UserDetailPage />} />
                <Route path="command-log" element={<CommandLog />} />
                <Route path="businesses" element={<Businesses />} />
                <Route path="businesses/:businessId" element={<BusinessDetailPage />} />
                <Route path="businesses/:businessId/:tab" element={<BusinessDetailPage />} />
                <Route path="creatives" element={<Creatives />} />
                <Route path="promo-codes" element={<PromoCodes />} />
                <Route path="content-engine" element={<ContentEngine />} />
                <Route path="social-gateways" element={<SocialGateways />} />
                <Route path="meta-migration" element={<MetaMigration />} />
                <Route path="content-engine/benchmarks/:benchmarkId" element={<BenchmarkQueue />} />
                <Route
                  path="content-engine/benchmarks/:benchmarkId/scoreboard"
                  element={<BenchmarkScoreboard />}
                />
                {/* Legacy /platform/playground/* paths land on the renamed Content Engine
                    page so any open tabs, in-product Links, or shared URLs keep working.
                    The splat preserves :benchmarkId (and trailing /scoreboard) so deep
                    links to a specific grading queue or scoreboard still resolve. */}
                <Route
                  path="playground"
                  element={<Navigate to="/platform/content-engine" replace />}
                />
                <Route
                  path="playground/benchmarks"
                  element={<Navigate to="/platform/content-engine?tab=benchmarks" replace />}
                />
                <Route
                  path="playground/benchmarks/*"
                  element={<SplatRedirect to="/platform/content-engine/benchmarks" />}
                />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="client-policy" element={<ClientPolicy />} />
                <Route path="referral" element={<Referral />} />
                <Route path="system-health" element={<SystemHealth />} />
              </Route>

              {/* Legacy redirects for old routes */}
              <Route path="funnel" element={<Navigate to="/metrics/funnel" replace />} />
              <Route path="campaigns" element={<Navigate to="/metrics/campaigns" replace />} />
              <Route path="engagement" element={<Navigate to="/metrics/engagement" replace />} />
              <Route path="taka-tv" element={<Navigate to="/metrics/taka-tv" replace />} />
              <Route path="taka-news" element={<Navigate to="/metrics/taka-news" replace />} />
              <Route path="users" element={<Navigate to="/platform/users" replace />} />
              <Route path="users/*" element={<SplatRedirect to="/platform/users" />} />
              <Route path="agents" element={<Navigate to="/platform/businesses" replace />} />
              <Route path="agents/*" element={<SplatRedirect to="/platform/businesses" />} />
              <Route path="api-keys" element={<Navigate to="/platform/api-keys" replace />} />
              <Route
                path="client-policy"
                element={<Navigate to="/platform/client-policy" replace />}
              />
            </Route>
          </Routes>
        </Suspense>
      )
    } else {
      return (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginLayout />} />
            {/* TakaTV with API key can work without admin login */}
            <Route path="/ttv" element={<TakaTV />} />
            <Route path="*" element={<LoginLayout />} />
          </Routes>
        </Suspense>
      )
    }
  }, [isLoggedIn, isHydrating])

  return (
    <div className="min-h-screen">
      {/* Command palette for quick navigation (Cmd+K / Ctrl+K) */}
      {isLoggedIn && <CommandPalette />}

      {routes}

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
    </div>
  )
}

export const App = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <TooltipProvider delayDuration={200}>
            <PresentationRouteBoundary>
              <AppContent />
            </PresentationRouteBoundary>
          </TooltipProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  )
}

export default App
